/* ApiClient wiring (QueryClient factory lives in customerQueryClient.ts for Fast Refresh).
 *
 * Cache keys are tenant-coded (see queryKeys.ts) so a future "tenant switch"
 * simply resets the tenant-prefixed slice via
 * `queryClient.invalidateQueries({ queryKey: ['customer', oldTenantCode] })`.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { ApiClient } from '../api/ApiClient.js';
import type { RuntimeConfig } from '../../config/runtimeConfig.js';
import { endCustomerSessionGlobal } from '../session/customerAuthSync.js';
import { handleUnauthorizedForPath } from '../session/sessionRefresh.js';
import { useTenantCode } from '../tenant/TenantProvider.js';

const ApiClientContext = createContext<ApiClient | null>(null);

interface AppDataProviderProps {
  config: RuntimeConfig;
  children: ReactNode;
}

/** ApiClient only — mount inside QueryClientProvider + CustomerSessionProvider. */
export function AppDataProvider({
  config,
  children,
}: AppDataProviderProps): JSX.Element {
  return <InnerApiClient config={config}>{children}</InnerApiClient>;
}

interface InnerApiClientProps {
  config: RuntimeConfig;
  children: ReactNode;
}

function InnerApiClient({ config, children }: InnerApiClientProps): JSX.Element {
  const tenantCode = useTenantCode();
  const apiClient = useMemo(
    () =>
      new ApiClient({
        config,
        getToken: () => null,
        getTenantCode: () => tenantCode,
        // G3 / Spec A cookie-first: memory JWT is non-authority — never attach
        // home Bearer for session (credentials jar only). Dual transitional
        // Bearer attach stays opt-in via tests / explicit ApiClient wiring.
        shouldAttachCustomerBearer: () => false,
        onUnauthorized: async (path, code, message) => {
          const action = await handleUnauthorizedForPath(config, path, code, message);
          if (action === 'end') {
            await endCustomerSessionGlobal({
              broadcast: true,
              callApi: false,
              reason: 'revoked',
            });
          }
          return action;
        },
      }),
    [config, tenantCode],
  );

  return (
    <ApiClientContext.Provider value={apiClient}>{children}</ApiClientContext.Provider>
  );
}

export function useApiClient(): ApiClient {
  const ctx = useContext(ApiClientContext);
  if (ctx === null) {
    throw new Error('useApiClient must be used inside <AppDataProvider>');
  }
  return ctx;
}
